import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import AdminLayout from '../components/AdminLayout';
import ConfirmDialog from '../components/ConfirmDialog';
import { Plus, Pencil, Trash2, X, Check, Loader2, Tag, Globe, Search, Database } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

const AdminBrands = () => {
  const { getAuthHeaders } = useAuth();
  const [brands, setBrands] = useState([]);
  const [trustedDomains, setTrustedDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingBrand, setEditingBrand] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', domain: '', aliases: '' });
  const [adding, setAdding] = useState(false);
  const [newBrand, setNewBrand] = useState({ name: '', domain: '', aliases: '' });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activeTab, setActiveTab] = useState('brands'); // 'brands' | 'domains'
  const [newDomain, setNewDomain] = useState('');
  const [addingDomain, setAddingDomain] = useState(false);
  const [deleteDomainTarget, setDeleteDomainTarget] = useState(null);

  useEffect(() => {
    fetchBrands();
    fetchTrustedDomains();
  }, []);

  const fetchBrands = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/brands`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setBrands(data.brands || []);
      }
    } catch (err) {
      console.error('Failed to fetch brands:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrustedDomains = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/trusted-domains`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setTrustedDomains(data.domains || []);
      }
    } catch (err) {
      console.error('Failed to fetch domains:', err);
    }
  };

  const seedBrands = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/brands/seed`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      toast.success(data.message);
      fetchBrands();
      fetchTrustedDomains();
    } catch (err) {
      toast.error('Failed to seed brands');
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async () => {
    if (!newBrand.name.trim() || !newBrand.domain.trim()) {
      toast.error('Name and domain are required');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/brands`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newBrand.name.trim(),
          domain: newBrand.domain.trim(),
          aliases: newBrand.aliases ? newBrand.aliases.split(',').map(a => a.trim()).filter(Boolean) : []
        })
      });
      if (res.ok) {
        toast.success('Brand added');
        setNewBrand({ name: '', domain: '', aliases: '' });
        setAdding(false);
        fetchBrands();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to add brand');
      }
    } catch (err) {
      toast.error('Failed to add brand');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand.brand_id);
    setEditForm({
      name: brand.name,
      domain: brand.domain,
      aliases: (brand.aliases || []).join(', ')
    });
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/brands/${editingBrand}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editForm.name.trim(),
          domain: editForm.domain.trim(),
          aliases: editForm.aliases ? editForm.aliases.split(',').map(a => a.trim()).filter(Boolean) : []
        })
      });
      if (res.ok) {
        toast.success('Brand updated');
        setEditingBrand(null);
        fetchBrands();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to update');
      }
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/brands/${deleteTarget.brand_id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success('Brand deleted');
        fetchBrands();
      }
    } catch (err) {
      toast.error('Failed to delete');
    }
    setDeleteTarget(null);
  };

  // Trusted domains
  const handleAddDomain = async () => {
    if (!newDomain.trim()) return;
    setAddingDomain(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/trusted-domains`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: newDomain.trim() })
      });
      if (res.ok) {
        toast.success('Domain added');
        setNewDomain('');
        fetchTrustedDomains();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to add');
      }
    } catch (err) {
      toast.error('Failed to add domain');
    } finally {
      setAddingDomain(false);
    }
  };

  const handleDeleteDomain = async () => {
    if (!deleteDomainTarget) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/trusted-domains/${deleteDomainTarget.domain_id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success('Domain removed');
        fetchTrustedDomains();
      }
    } catch (err) {
      toast.error('Failed to delete');
    }
    setDeleteDomainTarget(null);
  };

  const filteredBrands = brands.filter(b => 
    b.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.domain.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (b.aliases || []).some(a => a.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const filteredDomains = trustedDomains.filter(d =>
    d.domain.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-sage" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-charcoal">Brands & Domains</h1>
            <p className="text-sm text-gray-soft mt-1">Manage brand-to-domain mappings for Smart Add image search</p>
          </div>
          <div className="flex items-center gap-2">
            {brands.length === 0 && (
              <button
                onClick={seedBrands}
                disabled={saving}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors flex items-center gap-2 text-sm"
              >
                <Database size={16} />
                Seed Default Brands
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 border-b border-beige">
          <button
            onClick={() => setActiveTab('brands')}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'brands' 
                ? 'border-sage text-sage' 
                : 'border-transparent text-gray-soft hover:text-charcoal'
            }`}
          >
            <Tag size={16} className="inline mr-2" />
            Brands ({brands.length})
          </button>
          <button
            onClick={() => setActiveTab('domains')}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'domains' 
                ? 'border-sage text-sage' 
                : 'border-transparent text-gray-soft hover:text-charcoal'
            }`}
          >
            <Globe size={16} className="inline mr-2" />
            Trusted Domains ({trustedDomains.length})
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-soft" />
          <input
            type="text"
            placeholder={activeTab === 'brands' ? "Search brands..." : "Search domains..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-beige rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/30 focus:border-sage"
          />
        </div>

        {/* Brands Tab */}
        {activeTab === 'brands' && (
          <div className="space-y-4">
            {/* Add Brand Button */}
            {!adding ? (
              <button
                onClick={() => setAdding(true)}
                className="w-full py-3 border-2 border-dashed border-beige rounded-xl text-gray-soft hover:border-sage hover:text-sage transition-colors flex items-center justify-center gap-2"
              >
                <Plus size={18} />
                Add Brand
              </button>
            ) : (
              <div className="p-4 border border-beige rounded-xl bg-cream/30 space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <input
                    type="text"
                    placeholder="Brand Name"
                    value={newBrand.name}
                    onChange={(e) => setNewBrand(prev => ({ ...prev, name: e.target.value }))}
                    className="px-3 py-2 border border-beige rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/30 focus:border-sage"
                  />
                  <input
                    type="text"
                    placeholder="Domain (e.g., altenew.com)"
                    value={newBrand.domain}
                    onChange={(e) => setNewBrand(prev => ({ ...prev, domain: e.target.value }))}
                    className="px-3 py-2 border border-beige rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/30 focus:border-sage"
                  />
                  <input
                    type="text"
                    placeholder="Aliases (comma-separated)"
                    value={newBrand.aliases}
                    onChange={(e) => setNewBrand(prev => ({ ...prev, aliases: e.target.value }))}
                    className="px-3 py-2 border border-beige rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/30 focus:border-sage"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => { setAdding(false); setNewBrand({ name: '', domain: '', aliases: '' }); }}
                    className="px-3 py-1.5 text-sm text-gray-soft hover:text-charcoal"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAdd}
                    disabled={saving}
                    className="px-4 py-1.5 bg-sage text-white rounded-lg text-sm hover:bg-sage-dark disabled:opacity-50"
                  >
                    {saving ? <Loader2 size={14} className="animate-spin" /> : 'Add'}
                  </button>
                </div>
              </div>
            )}

            {/* Brands List */}
            <div className="border border-beige rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-cream/50">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-charcoal">Brand</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-charcoal">Domain</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-charcoal">Aliases</th>
                    <th className="w-24 px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-beige">
                  {filteredBrands.map(brand => (
                    <tr key={brand.brand_id} className="hover:bg-cream/30">
                      {editingBrand === brand.brand_id ? (
                        <>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={editForm.name}
                              onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                              className="w-full px-2 py-1 border border-beige rounded focus:outline-none focus:ring-1 focus:ring-sage"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={editForm.domain}
                              onChange={(e) => setEditForm(prev => ({ ...prev, domain: e.target.value }))}
                              className="w-full px-2 py-1 border border-beige rounded focus:outline-none focus:ring-1 focus:ring-sage"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={editForm.aliases}
                              onChange={(e) => setEditForm(prev => ({ ...prev, aliases: e.target.value }))}
                              className="w-full px-2 py-1 border border-beige rounded focus:outline-none focus:ring-1 focus:ring-sage"
                              placeholder="comma-separated"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={handleSaveEdit}
                                disabled={saving}
                                className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                              >
                                <Check size={16} />
                              </button>
                              <button
                                onClick={() => setEditingBrand(null)}
                                className="p-1.5 text-gray-soft hover:bg-cream rounded"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-3 text-sm text-charcoal font-medium">{brand.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-soft">{brand.domain}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {(brand.aliases || []).map((alias, i) => (
                                <span key={i} className="px-2 py-0.5 bg-cream text-xs text-gray-soft rounded">
                                  {alias}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleEdit(brand)}
                                className="p-1.5 text-gray-soft hover:text-charcoal hover:bg-cream rounded"
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                onClick={() => setDeleteTarget(brand)}
                                className="p-1.5 text-gray-soft hover:text-red-500 hover:bg-red-50 rounded"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                  {filteredBrands.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-gray-soft">
                        {brands.length === 0 ? 'No brands yet. Click "Seed Default Brands" to get started.' : 'No brands match your search.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Trusted Domains Tab */}
        {activeTab === 'domains' && (
          <div className="space-y-4">
            {/* Add Domain */}
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Add trusted domain (e.g., craftstore.com)"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddDomain()}
                className="flex-1 px-4 py-2.5 border border-beige rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/30 focus:border-sage"
              />
              <button
                onClick={handleAddDomain}
                disabled={addingDomain || !newDomain.trim()}
                className="px-4 py-2.5 bg-sage text-white rounded-lg hover:bg-sage-dark disabled:opacity-50 flex items-center gap-2"
              >
                {addingDomain ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                Add
              </button>
            </div>

            {/* Domains List */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {filteredDomains.map(domain => (
                <div
                  key={domain.domain_id}
                  className="flex items-center justify-between px-3 py-2 bg-cream/50 border border-beige rounded-lg group"
                >
                  <span className="text-sm text-charcoal truncate">{domain.domain}</span>
                  <button
                    onClick={() => setDeleteDomainTarget(domain)}
                    className="p-1 text-gray-soft hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>

            {filteredDomains.length === 0 && (
              <p className="text-center text-gray-soft py-8">
                {trustedDomains.length === 0 ? 'No trusted domains. Seed default brands to populate.' : 'No domains match your search.'}
              </p>
            )}

            <p className="text-xs text-gray-soft mt-4">
              Trusted domains are used to filter image search results. Images from these domains are prioritized in Smart Add.
            </p>
          </div>
        )}
      </div>

      {/* Delete Brand Confirmation */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Brand"
        message={`Remove "${deleteTarget?.name}" from the brand list? This won't affect existing stash items.`}
        cancelLabel="Cancel"
        confirmLabel="Delete"
        confirmVariant="danger"
      />

      {/* Delete Domain Confirmation */}
      <ConfirmDialog
        isOpen={!!deleteDomainTarget}
        onClose={() => setDeleteDomainTarget(null)}
        onConfirm={handleDeleteDomain}
        title="Remove Trusted Domain"
        message={`Remove "${deleteDomainTarget?.domain}" from trusted domains? Images from this domain will no longer be prioritized.`}
        cancelLabel="Cancel"
        confirmLabel="Remove"
        confirmVariant="warning"
      />
    </AdminLayout>
  );
};

export default AdminBrands;
